import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useRefundInvoiceAmountMutation } from '~entities/student/api/refund-invoice-amount';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { Textarea } from '~shared/shadcn/ui/textarea';
import { getErrorMessage } from '~shared/utils/error-handler';

type RefundMode = 'stripe' | 'manual' | 'online';

interface RefundDialogProps {
  id: string;
  amount: string;
  mode?: RefundMode;
  invoiceNumber?: string;
}

export const RefundDialog = ({
  id,
  amount,
  mode = 'stripe',
  invoiceNumber,
}: RefundDialogProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(amount);
  const [error, setError] = useState('');
  const [isFullAmount, setIsFullAmount] = useState(mode !== 'stripe');
  const [note, setNote] = useState('');

  const { data: studioDetails } = useGetStudioDetails();
  const isV2 = studioDetails?.isV2 === true;

  const { mutateAsync: refundInvoiceAmount, isPending } =
    useRefundInvoiceAmountMutation();

  const isManualReversal = mode === 'manual';
  const isOnlineSplitReversal = mode === 'online';
  const trimmedInvoiceNumber = invoiceNumber?.trim();
  // Prefer a human label (product / Stripe id). Never surface the Mongo _id in copy.
  const invoiceRefCopy = trimmedInvoiceNumber
    ? `invoice #${trimmedInvoiceNumber}`
    : 'this invoice';

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);

    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isManualReversal && !isOnlineSplitReversal && !isFullAmount) {
      if (!value) {
        setError('Please enter a number');
        return;
      }

      const numberValue = Number(value);
      if (isNaN(numberValue)) {
        setError('Please enter a valid number');
        return;
      }

      if (numberValue <= 0) {
        setError('Please enter a number greater than 0');
        return;
      }

      if (numberValue > Number(amount)) {
        setError(
          `Please enter a number less than or equal to the invoice amount: ${amount}`,
        );
        return;
      }
    }

    try {
      const enteredAmount = Number(value);
      const totalAmount = Number(amount);
      const isAmountEqualToFull =
        Number.isFinite(enteredAmount) &&
        Number.isFinite(totalAmount) &&
        Math.abs(enteredAmount - totalAmount) < 0.01;
      const shouldSendFullRefund =
        isManualReversal ||
        isOnlineSplitReversal ||
        isFullAmount ||
        isAmountEqualToFull;

      await refundInvoiceAmount({
        id,
        amount: shouldSendFullRefund ? amount : value,
        type: shouldSendFullRefund ? 'full' : 'partial',
        isV2,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(isManualReversal ? { reversalTarget: 'manual' } : {}),
        ...(isOnlineSplitReversal ? { reversalTarget: 'online' } : {}),
      });

      toast.success(
        isManualReversal
          ? 'Cash/check payment reversed successfully'
          : isOnlineSplitReversal
            ? 'Card payment refunded successfully'
            : 'Invoice refunded successfully',
      );
      setOpen(false);
      setValue('');
      setError('');
      setNote('');
    } catch (error) {
      console.error('Error refunding invoice:', error);
      const backendErrorMessage = getErrorMessage(error, 'Failed to refund');
      setError(backendErrorMessage);
      toast.error(backendErrorMessage);
    }
  };

  const triggerLabel = isManualReversal
    ? 'Reverse Cash/Check'
    : isOnlineSplitReversal
      ? 'Refund Card'
      : 'Refund';

  const dialogTitle = isManualReversal
    ? 'Reverse Cash/Check Payment'
    : isOnlineSplitReversal
      ? 'Refund Card Payment'
      : 'Refund Amount';

  const dialogDescription = isManualReversal
    ? `This creates a negative credit memo and reopens ${invoiceRefCopy}. Check reversals add the amount to the parent wallet; cash reversals do not.`
    : isOnlineSplitReversal
      ? `This refunds the card portion through the payment provider and reopens ${invoiceRefCopy} for the remaining balance.`
      : 'Please fill the amount you want to refund.';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant='outline'>{triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          {isManualReversal ? (
            <div className='mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900'>
              Full reversal only. A negative invoice will be created and{' '}
              {invoiceRefCopy} will become collectible again.
            </div>
          ) : null}
          {isOnlineSplitReversal ? (
            <div className='mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900'>
              Only the card portion (${amount}) will be refunded. Any cash/check
              payment on this invoice stays in place.
            </div>
          ) : null}
          {!isManualReversal && !isOnlineSplitReversal ? (
            <>
              <div className='flex items-center space-x-2'>
                <Checkbox
                  id='fullAmount'
                  checked={isFullAmount}
                  onCheckedChange={(checked) => {
                    setIsFullAmount(!!checked);
                  }}
                />
                <Label htmlFor='fullAmount' className='text-sm font-medium'>
                  Full Amount
                </Label>
              </div>
              <div className='my-4 grid grid-cols-5 items-center gap-4 gap-y-2'>
                <Label htmlFor='number' className='col-span-2'>
                  Partial Amount
                </Label>
                <div className='col-span-3'>
                  <Input
                    id='number'
                    type='number'
                    value={value}
                    onChange={handleValueChange}
                    className={error ? 'border-red-500' : ''}
                    placeholder='Enter a number'
                    disabled={isFullAmount}
                  />
                </div>
                {error && (
                  <p className='col-span-3 col-start-3 text-sm text-red-500'>
                    {error}
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className='mb-4 text-sm text-muted-foreground'>
              Amount: ${amount}
              {error ? (
                <p className='mt-2 text-sm text-red-500'>{error}</p>
              ) : null}
            </div>
          )}
          <div className='mb-4 grid gap-2'>
            <Label htmlFor='refund-note' className='text-sm font-medium'>
              Reason / Note{' '}
              <span className='font-normal text-muted-foreground'>
                (optional)
              </span>
            </Label>
            <Textarea
              id='refund-note'
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
              placeholder='e.g. Duplicate charge, customer request…'
              rows={3}
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant='outline' disabled={isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type='submit' disabled={isPending}>
              {isPending && <Loader2 className='animate-spin' />}
              {triggerLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
